import type { FC } from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { MiniFlexTable } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { useSelectorTrigger } from "app/components/filters-bar/useSelectorTrigger";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { ChangePercentage, GroupTrafficShare, ReferringCategoryCell } from "app/components/React/Table/cells";
import { TrafficShare } from "app/components/React/Table/cells/TrafficShare";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { FooterComponent } from "app/components/widget/WidgetFooter";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { i18nFilter } from "app/filters/i18nFilter";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { CenteredFlexRow, FlexRow, RightFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { useCurrentStateAndParams } from "@uirouter/react";

const ContentWrapper = styled(CenteredFlexRow)`
    height: 284px;
`;

const HeaderStyle = styled(FlexRow)`
    justify-content: space-between;
    box-sizing: border-box;
    padding: 24px;
`;

const CtaWrapper = styled(RightFlexRow)`
    height: 52px;
    border-top: 1px solid ${colorsPalettes.carbon[100]};

    button {
        margin: 5px 8px;
    }
`;

const ChangePercentageWrapper = styled.div`
    justify-content: end;
`;

const TopAdDestinationWrapper = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    height: 384px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 4px;
`;

const StyledTable: any = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
        height: 232px;
        flex: 1;
    }

    .MiniFlexTable-cell {
        height: 40px;
        vertical-align: middle;
        font-size: 14px;
        &:not(:first-child) {
            border-top: 1px solid ${colorsPalettes.carbon[50]};
        }
    }

    .MiniFlexTable-column {
        &:first-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-left: 24px;
            }
        }

        &:last-of-type {
            .MiniFlexTable-headerCell,
            .MiniFlexTable-cell {
                padding-right: 24px;
            }
        }

        .MiniFlexTable-cell {
            > div {
                height: 100%;
                align-items: center;
                display: flex;
            }
        }
    }
    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

interface IWWOTopReferringWebsitesTableProps {
    unlockHook?: any;
    getCtaLink: (innerLevelPackage?: string) => { href: string; target: string };
    hideFooter?: boolean;
}

export const WWOTopReferringWebsitesTable: FC<IWWOTopReferringWebsitesTableProps> = ({
    getCtaLink,
    unlockHook = null,
    hideFooter = false,
}) => {
    const { params, state } = useCurrentStateAndParams();
    const isCompare = params.key.split(",").length > 1;
    const { country, key, isWWW } = params;
    let { duration } = params;
    const { totalApiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);
    if (isWWWithoutPermissions) {
        duration = BasicDurations.LAST_THREE_MONTHS;
    }
    const configId = proNavigator.getConfigId(state);
    const durationObject = DurationService.getDurationData(duration, undefined, configId);
    const { from, to, isWindow } = durationObject.forAPI;
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);
    const i18n = i18nFilter();
    const title = i18n("wa.wwo.topreferringwebsites.title");
    const tooltip = i18n("wa.wwo.topreferringwebsites.title.tooltip");
    const [data, setData] = useState<object[]>(null);
    const [columns, setColumns] = useState<object[]>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const is28d = duration === "28d";
    const { selectorHandlers } = useSelectorTrigger("wwo-top-referring-websites");
    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
            },
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country)?.text,
        },
        {
            filter: "webSource",
            value: "Total",
        },
    ];
    const ctaLink = getCtaLink();

    const transformData = (data) => {
        const state = proNavigator.getState(proNavigator.current());
        return data?.Data.map((record) => {
            const mainCategory = record?.Category.substring(0, record.Category.indexOf("/"));
            const category = record?.Category.substring(record.Category.indexOf("/") + 1);

            return {
                ...record,
                url: proNavigator.href(state, {
                    ...params,
                    key: record.Domain,
                }),
                SiteOrigins: record.ShareList
                    ? record.ShareList.reduce((acc, current, index) => {
                          return {
                              ...acc,
                              [chosenSites.sitelistForLegend()[index].name]: current,
                          };
                      }, {})
                    : record?.ShareList,
                category: category === mainCategory ? category + `  - Other` : category,
                categoryForLink: record?.Category.replace("/", "~"),
            };
        });
    };

    useEffect(() => {
        async function fetchData() {
            setData(null);

            const endpoint = `widgetApi/${totalApiController}/TopReferrals/Table`;

            try {
                const response = await DefaultFetchService.getInstance().get<{ Data: object[] }>(endpoint, {
                    country,
                    from,
                    includeSubDomains: isWWW === "*",
                    isWindow,
                    keys: key,
                    timeGranularity: is28d ? "Daily" : "Monthly",
                    to,
                    pageSize: 5,
                    webSource: devicesTypes.TOTAL,
                    orderBy: "TotalShare desc",
                    ...(isCompare && { IncludeCrossData: true }),
                });
                setData(transformData(response));
                setColumns(getTableColumns());
            } catch (error) {
                const err = error as Error & { status?: number };
                // 404 is the blocked or low-traffic domain response, rendered as an empty state
                if (err?.status !== 404) {
                    Sentry.errorTracker({
                        e: err,
                        message: "WWOTopReferringWebsitesTable fetchData failed",
                        level: "error",
                        params: {
                            extra: { key, country, endpoint },
                        },
                    });
                }
                setData([]);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [from, to, isWindow, country, key]);

    const getTableColumns = () => {
        const cols = isCompare
            ? [
                  {
                      field: "category",
                      cellComponent: (props) => (
                          <ReferringCategoryCell
                              {...props}
                              isIconHidden={true}
                              getLink={() =>
                                  proNavigator.href("digitalsuite_website_referrals_incomingtraffic", {
                                      ...params,
                                      referralsCategory: props.row.categoryForLink,
                                  })
                              }
                          />
                      ),
                      displayName: i18nFilter()("wa.wwo.topreferringwebsites.category"),
                  },
                  {
                      field: "Share",
                      displayName: i18n("wa.wwo.topreferringwebsites.total.share"),
                      headerComponent: DefaultCellHeader,
                      cellComponent: TrafficShare,
                  },
                  {
                      field: "SiteOrigins",
                      displayName: i18n("wa.wwo.topreferringwebsites.share.split"),
                      cellComponent: GroupTrafficShare,
                      isResizable: false,
                      sortable: false,
                  },
              ]
            : [
                  {
                      field: "Share",
                      displayName: i18n("wa.wwo.topreferringwebsites.share"),
                      headerComponent: DefaultCellHeader,
                      cellComponent: TrafficShare,
                  },
                  {
                      field: "Change",
                      displayName: i18nFilter()("wa.wwo.topreferringwebsites.change"),
                      type: "double",
                      format: "percentagesign",
                      cellComponent: (props) => (
                          <ChangePercentageWrapper>
                              <ChangePercentage {...props} />
                          </ChangePercentageWrapper>
                      ),
                      headerComponent: DefaultCellHeaderRightAlign,
                      minWidth: 85,
                      visible: true,
                  },
              ];

        return [
            {
                field: "Domain",
                displayName: i18n("wa.wwo.topreferringwebsites.domain"),
                headerComponent: DefaultCellHeader,
                cellComponent: ({ value, row }) => {
                    const props = {
                        displayName: "CoreWebsiteCell",
                        domain: value,
                        icon: row.Favicon,
                        internalLink: row.url,
                        hideTrackButton: true,
                    };
                    return <CoreWebsiteCell {...props} />;
                },
            },
            ...cols,
        ];
    };

    const ctaClicked = () => {
        allTrackers.trackEvent("Internal Link", "click", "Marketing Mix/Top Referring Websites/See more referrals");
    };

    const getComponent = () =>
        data && data.length > 0 && columns ? (
            <>
                <StyledTable
                    className="MiniFlexTable"
                    data={data}
                    columns={columns}
                />
                {hideFooter ? null : (
                    <CtaWrapper>
                        <FooterComponent
                            href={ctaLink.href}
                            onClick={ctaClicked}
                            text="wwo.reffering.websites.button"
                            unlockHook={unlockHook}
                            target={ctaLink.target}
                        />
                    </CtaWrapper>
                )}
            </>
        ) : (
            <ContentWrapper>
                <EmptyState
                    titleKey="wa.wwo.no.data.title"
                    subTitleKey="wa.wwo.no.data.sub.title"
                />
            </ContentWrapper>
        );

    return (
        <TopAdDestinationWrapper data-automation="wwo-top-referring-websites">
            <HeaderStyle>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subTitleFilters}
                    {...selectorHandlers}
                />
            </HeaderStyle>
            {isLoading ? (
                <ContentWrapper>
                    <WidgetLoader />
                </ContentWrapper>
            ) : (
                getComponent()
            )}
        </TopAdDestinationWrapper>
    );
};
